/**
 * InfoTab: 설정 다이얼로그 > 정보 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/InfoTab
 * Redux connect → useAppSelector 전환
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import { isMasterUser } from '@/store/slices/masterSlice';
import apiConfig from '@/api/config';
import SNNameCard from '@luna-sena/sena-namecard';
import { useDispatch } from 'react-redux';
import { toastActions } from '@/store/slices/toastSlice';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';
import { getShareUrl } from '@/utils/urlUtils';
import { formatDateTime } from '@/utils/formatUtils';

const DEFAULT_PROFILE = 'https://static.wehago.com/imgs/common/no_profile.png';

interface InfoTabProps {
  onEditMode?: () => void;
}

const InfoTab: React.FC<InfoTabProps> = ({ onEditMode }) => {
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const isMaster = isMasterUser(masterUsers, local.wehagoId);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileBoxRef = useRef<HTMLDivElement>(null);
  const isCopyingRef = useRef(false);
  const dispatch = useDispatch();

  const handleProfileToggle = useCallback(() => {
    setIsProfileOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileBoxRef.current && !profileBoxRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  // 시작일시 포맷
  const formattedStartDatetime = (() => {
    const timestamp = local.startDatetime;
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const days = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = days[date.getDay()];
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${year}년 ${month}월 ${day}일(${dayOfWeek}) ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  })();

  // 회의 ID 표시용 포맷: "123456789" → "123-456-789" (3자리 단위 하이픈)
  const formatConferenceId = (id?: string | null) => {
    if (!id) return '';
    const digits = String(id).replace(/\D/g, '');
    if (!digits) return String(id);
    return digits.replace(/(\d{3})(?=\d)/g, '$1-');
  };
  const formattedConferenceId = formatConferenceId(local.conferenceId);

  // 회의 정보 복사
  const handleCopyMeetingId = useCallback(async () => {
    if (isCopyingRef.current) return;

    const shareUrl = getShareUrl();
    const hasPassword = !local.isPublic && local.securityType === 'PASSWORD';

    let copyMsg = `회의명 : ${local.roomName || ''}`;
    if (local.startDatetime) {
      copyMsg += `\n시간 : ${formatDateTime(local.startDatetime)}`;
    }
    copyMsg += `\n링크 : ${shareUrl}`;
    if (local.conferenceId) {
      copyMsg += `\n회의 ID : ${formatConferenceId(local.conferenceId)}`;
    }
    if (hasPassword && local.joinCode) {
      copyMsg += `\n암호 : ${local.joinCode}`;
    }

    try {
      await navigator.clipboard.writeText(copyMsg);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = copyMsg;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    isCopyingRef.current = true;
    dispatch(toastActions.showSnackbar({
      type: 'success',
      message: '회의 정보가 복사되었습니다.',
    }));
    setTimeout(() => {
      isCopyingRef.current = false;
    }, 2000);
  }, [local.conferenceId, local.roomName, local.startDatetime, local.isPublic, local.securityType, local.joinCode, dispatch]);

  const creator = local.creatorInfo;

  const creatorName = creator
    ? (creator.rank_name
        ? `${creator.user_name} ${creator.rank_name}`
        : creator.user_name)
    : local.userName;

  const creatorProfileImg = toAbsoluteProfileUrl(creator?.profile_url) || DEFAULT_PROFILE;

  const creatorOrganization = creator?.full_path_list?.length
    ? creator.full_path_list.map((p) => p.organization_name).join(' > ')
    : undefined;

  const nameCardData = useMemo(() => ({
    imageUrl: creatorProfileImg,
    name: creator?.user_name ?? local.userName,
    position: creator?.rank_name || creator?.position_name || undefined,
    organization: creatorOrganization,
    email: creator?.user_email || creator?.user_default_email || undefined,
    tel: creator?.user_contact || undefined,
    isState: '0' as const,
  }), [creator, creatorProfileImg, creatorOrganization, local.userName]);

  // 연결된 회의 날짜 포맷: "2025.09.26 | 14:00~15:31"
  const linkedMeeting = local.linkedMeeting;
  const linkedMeetingDateStr = useMemo(() => {
    if (!linkedMeeting) return '';
    const parts: string[] = [];
    if (linkedMeeting.startDateTime) {
      const s = new Date(linkedMeeting.startDateTime);
      const dateStr = `${s.getFullYear()}.${String(s.getMonth() + 1).padStart(2, '0')}.${String(s.getDate()).padStart(2, '0')}`;
      const startTime = `${String(s.getHours()).padStart(2, '0')}:${String(s.getMinutes()).padStart(2, '0')}`;
      if (linkedMeeting.endDateTime) {
        const e = new Date(linkedMeeting.endDateTime);
        const endTime = `${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`;
        parts.push(dateStr, `${startTime}~${endTime}`);
      } else {
        parts.push(dateStr, startTime);
      }
    }
    return parts.join(' | ');
  }, [linkedMeeting]);

  // 연결된 회의 클릭 → 회의기록 페이지 새탭
  const handleOpenLinkedMeeting = useCallback(() => {
    if (!linkedMeeting) return;
    const baseUrl = apiConfig.portalBaseUrl || 'https://www.wehago.com';
    window.open(`${baseUrl}/wehagomeet/#/conversation-record?roomId=${encodeURIComponent(linkedMeeting.roomId)}`, '_blank');
  }, [linkedMeeting]);

  // 암호 표시 조건: 비공개 회의 + password 보안 타입 + joinCode 존재
  const showPassword = !local.isPublic
    && local.securityType === 'PASSWORD'
    && !!local.joinCode;

  return (
    <>
    <div className="rtcDialogSetupNew__info">
      <div>
        <dt>회의명</dt>
        <dd>
          <p className="infoText">{local.roomName || ''}</p>
        </dd>
      </div>
      {/* 연결된 회의: 연결된 회의가 있고 로그인 사용자인 경우만 노출 */}
      {linkedMeeting && !local.isGuest && (
        <div>
          <dt>연결된 회의</dt>
          <dd>
            <button type="button" className="rtcDialogSetupNew__infoLink" onClick={handleOpenLinkedMeeting}>
              <span className="rtcDialogSetupNew__infoLinkText">
                <span className="lunacon lunacon--link-outlined" />
                <span className="ellipsis">{linkedMeeting.name}</span>
              </span>
              {linkedMeetingDateStr && (
                <span className="rtcDialogSetupNew__infoLinkDate">
                  {linkedMeetingDateStr.split(' | ')[0]}
                  {linkedMeetingDateStr.includes(' | ') && (
                    <>
                      <span className="bar" />
                      {linkedMeetingDateStr.split(' | ')[1]}
                    </>
                  )}
                </span>
              )}
              <span className="rtcDialogSetupNew__infoLinkArrow">
                <span className="lunacon lunacon--arrowRight-outlined" />
              </span>
            </button>
          </dd>
        </div>
      )}
      <div>
        <dt>회의 ID</dt>
        <dd>
          <div className="inlineFlex">
            <p className="infoText">{formattedConferenceId}</p>
            <button type="button" className="iconBtn" onClick={handleCopyMeetingId}>
              <span className="lunacon lunacon--copy-outlined" />
              <span className="blind">복사</span>
            </button>
          </div>
        </dd>
      </div>
      <div>
        <dt>{local.isPublic ? '공개 회의' : '비공개 회의'}</dt>
        <dd>
          <p className="infoText">
            {local.isPublic
              ? '초대받지 않은 인원도 회의 링크나 회의 ID를 통해 회의에 참가할 수 있습니다.'
              : '회의 초대 링크 또는 암호를 통해 회의에 참가할 수 있습니다.'}
          </p>
        </dd>
      </div>
      {/* 암호: 비공개 회의 + 암호 설정된 경우만 노출 */}
      {showPassword && (
        <div>
          <dt>암호</dt>
          <dd>
            <p className="infoText">{local.joinCode}</p>
          </dd>
        </div>
      )}
      <div>
        <dt>시작일시</dt>
        <dd>
          <p className="infoText">{formattedStartDatetime}</p>
        </dd>
      </div>
      <div>
        <dt>개설자</dt>
        <dd>
          <div className="infoProfileBox" ref={profileBoxRef}>
            <button
              type="button"
              className="infoProfileBox__btn"
              onClick={handleProfileToggle}
            >
              <span className="common_newProfile">
                <img src={creatorProfileImg} alt="" />
              </span>
              <span className="name">{creatorName}</span>
            </button>
            <div
              className="infoProfileBox__popover"
              style={{ display: isProfileOpen ? 'block' : 'none' }}
            >
              <SNNameCard data={nameCardData} />
            </div>
          </div>
        </dd>
      </div>
    </div>
    {isMaster && (
    <div className="rtcDialogSetupNew__infoFooterBtn">
      <button
        type="button"
        className="common_round_btn rtcNew_btnType"
        onClick={onEditMode}
      >
        회의설정 변경하기
      </button>
    </div>
    )}
    </>
  );
};

export default InfoTab;
